import { supabase } from '@/integrations/supabase/client';

/**
 * Scanner authentication utilities for the /update-pass system
 * Provides secure authentication without requiring business owner login
 */

// ============================================
// OWNER-LEVEL KIOSK AUTH (for /kiosk page)
// ============================================

export interface KioskAuthResult {
  success: boolean;
  user_id?: string;
  businesses?: Array<{ id: string; name: string }>;
  error?: string;
}

export interface KioskSession {
  user_id: string;
  username: string;
  businesses: Array<{ id: string; name: string }>;
  selected_business_id: string | null;
  selected_business_name: string | null;
  authenticated_at: number;
  expires_at: number;
}

const KIOSK_SESSION_KEY = 'taginn_kiosk_session';
const KIOSK_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours

/**
 * Authenticate kiosk credentials (owner-level)
 */
export async function authenticateKioskCredentials(
  username: string,
  password: string
): Promise<KioskAuthResult> {
  try {
    if (!username || !password) {
      return { success: false, error: 'Username and password are required' };
    }

    // Use the SECURITY DEFINER function to validate credentials (bypasses RLS)
    const { data: validationResult, error: validationError } = await supabase
      .rpc('validate_kiosk_credentials', {
        p_username: username,
        p_password: password
      });

    if (validationError) {
      console.error('Kiosk validation error:', validationError);
      return { success: false, error: 'Authentication failed' };
    }

    // Check validation result
    const result = validationResult?.[0];
    if (!result || !result.is_valid) {
      return { success: false, error: result?.error_message || 'Invalid credentials' };
    }

    const userId = result.user_id;

    // Get all businesses owned by this user using a function that bypasses RLS
    const { data: businesses, error: bizError } = await supabase
      .rpc('get_user_businesses_for_kiosk', { p_user_id: userId });

    if (bizError) {
      console.error('Error fetching businesses:', bizError);
      return { success: false, error: 'Failed to load businesses' };
    }

    if (!businesses || businesses.length === 0) {
      return { success: false, error: 'No businesses found for this account' };
    }

    return {
      success: true,
      user_id: userId,
      businesses: businesses
    };
  } catch (error) {
    console.error('Kiosk authentication error:', error);
    return { success: false, error: 'Authentication failed' };
  }
}

/**
 * Create and store kiosk session (owner-level)
 */
export function createKioskSession(authResult: KioskAuthResult, username: string): void {
  if (!authResult.success || !authResult.user_id) return;

  const session: KioskSession = {
    user_id: authResult.user_id,
    username,
    businesses: authResult.businesses || [],
    selected_business_id: null,
    selected_business_name: null,
    authenticated_at: Date.now(),
    expires_at: Date.now() + KIOSK_SESSION_DURATION
  };

  try {
    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to store kiosk session:', error);
  }
}

/**
 * Get current kiosk session
 */
export function getKioskSession(): KioskSession | null {
  try {
    const sessionData = localStorage.getItem(KIOSK_SESSION_KEY);
    if (!sessionData) return null;

    const session: KioskSession = JSON.parse(sessionData);

    if (Date.now() > session.expires_at) {
      clearKioskSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Failed to get kiosk session:', error);
    clearKioskSession();
    return null;
  }
}

/**
 * Update selected business in kiosk session
 */
export function selectKioskBusiness(businessId: string, businessName: string): boolean {
  const session = getKioskSession();
  if (!session) return false;

  try {
    session.selected_business_id = businessId;
    session.selected_business_name = businessName;
    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    return true;
  } catch (error) {
    console.error('Failed to update kiosk session:', error);
    return false;
  }
}

/**
 * Clear kiosk session
 */
export function clearKioskSession(): void {
  try {
    localStorage.removeItem(KIOSK_SESSION_KEY);
  } catch (error) {
    console.error('Failed to clear kiosk session:', error);
  }
}

/**
 * Check if kiosk session is valid
 */
export function isValidKioskSession(): boolean {
  const session = getKioskSession();
  return session !== null;
}

/**
 * Check if kiosk session has a selected business
 */
export function hasSelectedKioskBusiness(): boolean {
  const session = getKioskSession();
  return session !== null && session.selected_business_id !== null;
}

/**
 * Extend kiosk session
 */
export function extendKioskSession(): boolean {
  const session = getKioskSession();
  if (!session) return false;

  try {
    session.expires_at = Date.now() + KIOSK_SESSION_DURATION;
    localStorage.setItem(KIOSK_SESSION_KEY, JSON.stringify(session));
    return true;
  } catch (error) {
    console.error('Failed to extend kiosk session:', error);
    return false;
  }
}

// ============================================
// BUSINESS-LEVEL SCANNER AUTH (for /update-pass)
// ============================================

export interface ScannerAuthResult {
  success: boolean;
  business_id?: string;
  business_name?: string;
  loyalty_programs?: any[]; // All loyalty programs for the business
  // Backwards compatibility for single program
  program_id?: string;
  program_name?: string;
  program_type?: string;
  error?: string;
}

export interface ScannerSession {
  business_id: string;
  business_name: string;
  loyalty_programs: any[]; // All accessible loyalty programs
  username: string;
  authenticated_at: number;
  expires_at: number;
  // Backwards compatibility
  program_id?: string;
  program_name?: string;
  program_type?: string;
}

// Session storage keys
const SCANNER_SESSION_KEY = 'taginn_scanner_session';
const SCANNER_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours in milliseconds

/**
 * Simple password hashing function using Web Crypto API
 * Note: For production, consider using a more robust solution like bcrypt
 */
export async function hashPassword(password: string, salt?: string): Promise<string> {
  // Generate salt if not provided
  if (!salt) {
    const saltArray = new Uint8Array(16);
    crypto.getRandomValues(saltArray);
    salt = Array.from(saltArray, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  // Create password hash using PBKDF2
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);
  const saltData = encoder.encode(salt);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordData,
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  
  const hashBuffer = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: saltData,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    256
  );
  
  const hashArray = new Uint8Array(hashBuffer);
  const hashHex = Array.from(hashArray, byte => byte.toString(16).padStart(2, '0')).join('');
  
  return `${salt}:${hashHex}`;
}

/**
 * Verify password against hash
 */
export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  try {
    const [salt, storedHash] = hash.split(':');
    if (!salt || !storedHash) return false;
    
    const computedHash = await hashPassword(password, salt);
    const [, computedHashPart] = computedHash.split(':');
    
    return computedHashPart === storedHash;
  } catch (error) {
    console.error('Password verification error:', error);
    return false;
  }
}

/**
 * Authenticate scanner credentials against the database
 */
export async function authenticateScannerCredentials(
  username: string, 
  password: string
): Promise<ScannerAuthResult> {
  try {
    if (!username || !password) {
      return { success: false, error: 'Username and password are required' };
    }

    // Check if credentials match business scanner credentials
    const { data: businesses, error: fetchError } = await supabase
      .from('businesses')
      .select('id, name, scanner_username, scanner_password, scanner_enabled')
      .eq('scanner_username', username)
      .eq('scanner_enabled', true)
      .limit(1);

    if (fetchError) {
      console.error('Database error during scanner auth:', fetchError);
      return { success: false, error: 'Authentication failed' };
    }

    if (!businesses || businesses.length === 0) {
      return { success: false, error: 'Invalid credentials' };
    }

    const business = businesses[0];

    // Verify password (plain text comparison)
    if (!business.scanner_password) {
      return { success: false, error: 'Scanner credentials not properly configured' };
    }

    const passwordValid = password === business.scanner_password;

    if (!passwordValid) {
      return { success: false, error: 'Invalid credentials' };
    }

    // Get all loyalty programs for this business (business-wide access)
    const { data: allPrograms, error: allProgramsError } = await supabase
      .from('loyalty_programs')
      .select('id, name, program_type')
      .eq('business_id', business.id)
      .eq('is_active', true)
      .is('deleted_at', null);

    if (allProgramsError) {
      console.error('Error fetching business loyalty programs:', allProgramsError);
    }

    const loyaltyPrograms = allPrograms || [];

    console.log(`Scanner auth success for business: ${business.name} (${loyaltyPrograms.length} programs accessible)`);

    // For backwards compatibility, use first program details if available
    const firstProgram = loyaltyPrograms[0];

    return {
      success: true,
      business_id: business.id,
      business_name: business.name,
      loyalty_programs: loyaltyPrograms,
      // For backwards compatibility, include the first program's details
      program_id: firstProgram?.id,
      program_name: firstProgram?.name,
      program_type: firstProgram?.program_type
    };

  } catch (error) {
    console.error('Scanner authentication error:', error);
    return { success: false, error: 'Authentication failed' };
  }
}

/**
 * Log authentication attempt (optional logging)
 */
async function logAuthAttempt(programId: string, username: string, success: boolean): Promise<void> {
  try {
    // This would use the log_scanner_auth_attempt function from the migration
    // For now, we'll just log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Scanner auth attempt: ${username} - ${success ? 'SUCCESS' : 'FAILED'} - Program: ${programId}`);
    }
    
    // In production, you could call the database function:
    // await supabase.rpc('log_scanner_auth_attempt', {
    //   p_program_id: programId,
    //   p_username: username,
    //   p_success: success
    // });
  } catch (error) {
    // Don't fail authentication due to logging errors
    console.warn('Failed to log auth attempt:', error);
  }
}

/**
 * Create and store scanner session
 */
export function createScannerSession(authResult: ScannerAuthResult, username: string): void {
  if (!authResult.success || !authResult.business_id) return;

  const session: ScannerSession = {
    business_id: authResult.business_id!,
    business_name: authResult.business_name!,
    loyalty_programs: authResult.loyalty_programs || [],
    username,
    authenticated_at: Date.now(),
    expires_at: Date.now() + SCANNER_SESSION_DURATION,
    // Backwards compatibility
    ...(authResult.program_id && {
      program_id: authResult.program_id,
      program_name: authResult.program_name,
      program_type: authResult.program_type
    })
  };

  try {
    localStorage.setItem(SCANNER_SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to store scanner session:', error);
  }
}

/**
 * Get current scanner session if valid
 */
export function getScannerSession(): ScannerSession | null {
  try {
    const sessionData = localStorage.getItem(SCANNER_SESSION_KEY);
    if (!sessionData) return null;

    const session: ScannerSession = JSON.parse(sessionData);
    
    // Check if session is expired
    if (Date.now() > session.expires_at) {
      clearScannerSession();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Failed to get scanner session:', error);
    clearScannerSession();
    return null;
  }
}

/**
 * Clear scanner session
 */
export function clearScannerSession(): void {
  try {
    localStorage.removeItem(SCANNER_SESSION_KEY);
  } catch (error) {
    console.error('Failed to clear scanner session:', error);
  }
}

/**
 * Check if current session is valid
 */
export function isValidScannerSession(): boolean {
  const session = getScannerSession();
  return session !== null;
}

/**
 * Extend current session (if valid)
 */
export function extendScannerSession(): boolean {
  const session = getScannerSession();
  if (!session) return false;

  try {
    session.expires_at = Date.now() + SCANNER_SESSION_DURATION;
    localStorage.setItem(SCANNER_SESSION_KEY, JSON.stringify(session));
    return true;
  } catch (error) {
    console.error('Failed to extend scanner session:', error);
    return false;
  }
}

/**
 * Generate a secure random password for scanner credentials
 */
export function generateScannerPassword(): string {
  // Generate a secure 12-character password with mixed case, numbers, and symbols
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
  let result = '';
  
  for (let i = 0; i < 12; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  return result;
}

/**
 * Validate scanner username format
 */
export function validateScannerUsername(username: string): { valid: boolean; error?: string } {
  if (!username) {
    return { valid: false, error: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters long' };
  }
  
  if (username.length > 50) {
    return { valid: false, error: 'Username must be less than 50 characters long' };
  }
  
  // Allow alphanumeric characters, hyphens, and underscores
  const validPattern = /^[a-zA-Z0-9_-]+$/;
  if (!validPattern.test(username)) {
    return { valid: false, error: 'Username can only contain letters, numbers, hyphens, and underscores' };
  }
  
  return { valid: true };
}

/**
 * Validate scanner password format
 */
export function validateScannerPassword(password: string): { valid: boolean; error?: string } {
  if (!password) {
    return { valid: false, error: 'Password is required' };
  }
  
  if (password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters long' };
  }
  
  if (password.length > 100) {
    return { valid: false, error: 'Password must be less than 100 characters long' };
  }
  
  return { valid: true };
}